'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { useInfiniteQuery } from 'react-query';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import FilterButtonWithIcon from '../../../../components/FilterButtonWithIcon';
import FiltersModal from '../../../../components/FiltersModal';
import Loading from '../../../../components/Loading';
import LoadingContainer from '../../../../components/LoadingContainer';
import ResourceItem from '../../../../components/ResourceItem';
import WarningContainer from '../../../../components/WarningContainer';
import WarningText from '../../../../components/WarningText';
import Wrapper from '../../../../components/Wrapper';
import { amplitudeInstance } from '../../../../config/amplitude';
import buildRouteUrl from '../../../../helpers/buildRouteUrl';
import useFiltersQuery from '../../../../hooks/useFiltersQuery';
import useIntl from '../../../../hooks/useIntl';
import useModal from '../../../../hooks/useModal';
import api from '../../../../services/api';
import { BasicResource, Genre, Metadata } from '../../../../types/types';

interface PropTypes {
  genre: Genre;
  type: 'movies' | 'tv';
}

function ClientGenres({ genre, type }: PropTypes) {
  const t = useTranslations();
  const intl = useIntl();

  const {
    onOpen: onOpenFiltersModal,
    isOpen: isFiltersModalOpen,
    onClose: onCloseFiltersModal
  } = useModal();

  const [filtersQuery, setFiltersQuery] = useFiltersQuery();

  const [totalMovies, setTotalMovies] = useState(0);
  const [totalTvs, setTotalTvs] = useState(0);

  const containerRef = useRef<any>(null);

  const genreMoviesQuery = useInfiniteQuery<{
    data: BasicResource[];
    metadata: Metadata;
  }>(
    [`genres-${genre.id}-movies`, filtersQuery.watchProvidersIds],
    async ({ pageParam: offset = 0 }) => {
      const response = await api.get(
        `/v1/genres/${genre.id}/movies/?offset=${offset || 0}&limit=50`,
        {
          headers: {
            'accept-language': intl.full_language
          },
          params: {
            with_watch_providers: filtersQuery.watchProvidersIds
          }
        }
      );

      return response.data;
    },
    {
      enabled: !!genre.id,
      getNextPageParam: lastGroup => {
        if (lastGroup.metadata.total > lastGroup.metadata.offset) {
          return lastGroup.metadata.offset + 50;
        }

        return false;
      }
    }
  );

  useEffect(() => {
    if (genreMoviesQuery.isSuccess) {
      // eslint-disable-next-line no-unused-expressions
      genreMoviesQuery.data?.pages.forEach(group => {
        setTotalMovies(prevState => prevState + group.data.length);
      });
    }
  }, [genreMoviesQuery.isSuccess, genreMoviesQuery.data]);

  const genreTvsQuery = useInfiniteQuery<{
    data: BasicResource[];
    metadata: Metadata;
  }>(
    [`genres-${genre.id}-tv`, filtersQuery.watchProvidersIds],
    async ({ pageParam: offset = 0 }) => {
      const response = await api.get(
        `/v1/genres/${genre.id}/tv/?offset=${offset || 0}&limit=50`,
        {
          headers: {
            'accept-language': intl.full_language
          },
          params: {
            with_watch_providers: filtersQuery.watchProvidersIds
          }
        }
      );

      return response.data;
    },
    {
      enabled: !!genre.id,
      getNextPageParam: lastGroup => {
        if (lastGroup.metadata.total > lastGroup.metadata.offset) {
          return lastGroup.metadata.offset + 50;
        }
        return false;
      }
    }
  );

  useEffect(() => {
    if (genreTvsQuery.isSuccess) {
      // eslint-disable-next-line no-unused-expressions
      genreTvsQuery.data?.pages.forEach(group => {
        setTotalTvs(prevState => prevState + group.data.length);
      });
    }
  }, [genreTvsQuery.isSuccess, genreTvsQuery.data]);

  const onEndReached = useCallback(() => {
    if (type === 'movies') {
      if (
        genreMoviesQuery.hasNextPage &&
        !genreMoviesQuery.isFetchingNextPage
      ) {
        genreMoviesQuery.fetchNextPage();
      }
    }

    if (type === 'tv') {
      if (genreTvsQuery.hasNextPage && !genreTvsQuery.isFetchingNextPage) {
        genreTvsQuery.fetchNextPage();
      }
    }
  }, [
    genreMoviesQuery.hasNextPage,
    genreMoviesQuery.isFetchingNextPage,
    genreTvsQuery.hasNextPage,
    genreTvsQuery.isFetchingNextPage
  ]);

  return (
    <>
      <FiltersModal
        isOpen={isFiltersModalOpen}
        onClose={onCloseFiltersModal}
        filters={{ watchProviders: true }}
        onSaveFilters={selectedFilters => {
          if (selectedFilters.watchProvidersIds) {
            genreMoviesQuery.remove();
            setFiltersQuery({
              watchProvidersIds: selectedFilters.watchProvidersIds
            });
          }
        }}
      />
      <Wrapper>
        <BottomScrollListener onBottom={onEndReached} offset={150}>
          {() => (
            <div
              ref={containerRef}
              className="flex flex-col w-full bg-gray-600 px-4 md:px-10"
            >
              <div className="flex flex-col bg-gray-600 mt-3 pb-1 w-full z-[8]">
                <div>
                  <h1 className="text-white text-3xl font-bold">
                    {genre.name}
                  </h1>
                  <p className="text-gray-500 mt-2">
                    {type === 'movies' &&
                      t('genres.movies.description', {
                        name: genre.name
                      })}

                    {type === 'tv' &&
                      t('genres.tv_shows.description', {
                        name: genre.name
                      })}
                  </p>
                </div>

                <div className="flex flex-row mt-4">
                  <Link
                    href={`/genres/${genre.id}/movies`}
                    replace
                    className={`text-lg mr-2 ${
                      type === 'movies'
                        ? 'text-white font-bold'
                        : 'text-gray-200'
                    }`}
                  >
                    {t('genres_titles.tabs.movies')}
                  </Link>

                  <Link
                    href={`/genres/${genre.id}/tv`}
                    replace
                    className={`text-lg ${
                      type === 'tv' ? 'text-white font-bold' : 'text-gray-200'
                    }`}
                  >
                    {t('genres_titles.tabs.tvs')}
                  </Link>
                </div>

                <div className="my-2">
                  <FilterButtonWithIcon onClick={onOpenFiltersModal} />
                </div>
              </div>

              {type === 'movies' && (
                <div>
                  {genreMoviesQuery.isLoading && (
                    <LoadingContainer>
                      <Loading />
                    </LoadingContainer>
                  )}

                  {genreMoviesQuery.isSuccess &&
                    genreMoviesQuery.data.pages[0].metadata.total === 0 && (
                      <WarningContainer>
                        <WarningText>
                          {t('genres_titles.warnings.movie_not_found')}
                        </WarningText>
                      </WarningContainer>
                    )}

                  {genreMoviesQuery.isSuccess && totalMovies > 0 && (
                    <div className="custom-grid w-full h-full gap-x-6 gap-y-3 pb-4">
                      {genreMoviesQuery.data?.pages.map((group, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                          {group.data.map(item => {
                            return (
                              <Link
                                key={item._id}
                                href={buildRouteUrl(`/movie/${item._id}`)}
                              >
                                <ResourceItem
                                  data={item}
                                  onClick={() => {
                                    amplitudeInstance.logEvent(
                                      'Go To Content Page From Genre Contents Page',
                                      {
                                        content_id: item._id,
                                        content_type: type,
                                        genre_id: genre.id
                                      }
                                    );
                                  }}
                                />
                              </Link>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {type === 'tv' && (
                <div>
                  {genreTvsQuery.isLoading && (
                    <LoadingContainer>
                      <Loading />
                    </LoadingContainer>
                  )}

                  {genreTvsQuery.isSuccess &&
                    genreTvsQuery.data.pages[0].metadata.total === 0 && (
                      <WarningContainer>
                        <WarningText>
                          {t('genres_titles.warnings.tv_not_found')}
                        </WarningText>
                      </WarningContainer>
                    )}

                  {genreTvsQuery.isSuccess && totalTvs > 0 && (
                    <div className="custom-grid w-full h-full gap-x-6 gap-y-3 pb-4">
                      {genreTvsQuery.data?.pages.map((group, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                          {group.data.map(item => {
                            return (
                              <Link
                                key={item._id}
                                href={buildRouteUrl(`/tv/${item._id}`)}
                              >
                                <ResourceItem
                                  key={item._id}
                                  data={item}
                                  onClick={() => {
                                    amplitudeInstance.logEvent(
                                      'Go To Content Page From Genre Contents Page',
                                      {
                                        content_id: item._id,
                                        content_type: type,
                                        genre_id: genre.id
                                      }
                                    );
                                  }}
                                />
                              </Link>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </BottomScrollListener>
      </Wrapper>
    </>
  );
}

export default ClientGenres;
